<div [class]="containerClasses">
  <!-- Toolbar -->
  <div class="acs-data-grid__toolbar">
    <!-- Main Toolbar Row -->
    <div class="acs-data-grid__toolbar-main">
      <!-- Search Bar -->
      <div class="acs-data-grid__search" *ngIf="searchable">
        <div class="acs-data-grid__search-input-wrapper">
          <input 
            type="text"
            class="acs-data-grid__search-input"
            [placeholder]="searchPlaceholder"
            [(ngModel)]="searchValue"
            (input)="onSearch(searchValue)"
            aria-label="Search data">
          <button 
            *ngIf="searchValue"
            class="acs-data-grid__search-clear"
            (click)="clearSearch()"
            aria-label="Clear search">
            ✕
          </button>
          <div class="acs-data-grid__search-icon">🔍</div>
        </div>
      </div>

      <!-- Actions -->
      <div class="acs-data-grid__actions" *ngIf="filterable || exportable">
        <!-- Filter Toggle -->
        <button 
          *ngIf="filterable"
          class="acs-data-grid__action-button"
          [class.acs-data-grid__action-button--active]="showFilterRow"
          (click)="toggleFilterRow()"
          [attr.aria-label]="showFilterRow ? 'Hide filters' : 'Show filters'">
          <span class="acs-data-grid__action-icon acs-data-grid__funnel-icon"></span>
          <span class="acs-data-grid__action-label">{{ showFilterRow ? 'Hide Filters' : 'Show Filters' }}</span>
        </button>

        <!-- View Mode Toggle -->
        <button 
          class="acs-data-grid__action-button"
          (click)="toggleViewMode()"
          [attr.aria-label]="viewMode === 'grid' ? 'Switch to card view' : 'Switch to grid view'">
          <span class="acs-data-grid__action-icon">{{ viewMode === 'grid' ? '⊞' : '☰' }}</span>
          <span class="acs-data-grid__action-label">{{ viewMode === 'grid' ? 'Card View' : 'Grid View' }}</span>
        </button>

        <!-- Export Options -->
        <div class="acs-data-grid__export" *ngIf="exportable">
          <button 
            class="acs-data-grid__action-button acs-data-grid__export-toggle"
            (click)="toggleExportMenu()"
            [attr.aria-expanded]="showExportMenu"
            [class.acs-data-grid__action-button--active]="showExportMenu">
            <span class="acs-data-grid__action-icon">📊</span>
            <span class="acs-data-grid__action-label">Export</span>
            <span class="acs-data-grid__action-arrow" [class.acs-data-grid__action-arrow--open]="showExportMenu">▼</span>
          </button>
          <div 
            class="acs-data-grid__export-menu"
            [class.acs-data-grid__export-menu--open]="showExportMenu">
            <button (click)="onExport('csv')" class="acs-data-grid__export-option">
              <span class="acs-data-grid__export-icon">📄</span>
              <span>CSV</span>
            </button>
            <button (click)="onExport('excel')" class="acs-data-grid__export-option">
              <span class="acs-data-grid__export-icon">📊</span>
              <span>Excel</span>
            </button>
            <button (click)="onExport('pdf')" class="acs-data-grid__export-option">
              <span class="acs-data-grid__export-icon">📑</span>
              <span>PDF</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Selection Info Row -->
    <div class="acs-data-grid__selection-row" *ngIf="selectable === 'multiple' && selectedItems.size > 0">
      <div class="acs-data-grid__selection-info">
        <span class="acs-data-grid__selection-count">{{ selectedItems.size }} item(s) selected</span>
        <button 
          class="acs-data-grid__selection-action"
          (click)="clearSelection()"
          aria-label="Clear selection">
          Clear Selection
        </button>
      </div>
    </div>
  </div>

  <!-- Loading Overlay -->
  <div class="acs-data-grid__loading" *ngIf="effectiveLoading">
    <div class="acs-data-grid__loading-content">
      <div class="acs-data-grid__spinner"></div>
      <span>{{ loadingText }}</span>
    </div>
  </div>

  <!-- Grid View -->
  <div class="acs-data-grid__container" *ngIf="viewMode === 'grid'">
    <!-- Scroll Container -->
    <div 
      #scrollContainer
      class="acs-data-grid__scroll-container"
      [style.height.px]="fixedContainerHeight">

      <!-- Virtual Scroll Spacer -->
      <div 
        *ngIf="virtualScrolling && !serverSide"
        class="acs-data-grid__spacer"
        [style.height.px]="totalHeight">
      </div>

      <!-- Table -->
      <table class="acs-data-grid__table">
        <!-- Header -->
        <thead class="acs-data-grid__header" [class.acs-data-grid__header--sticky]="stickyHeader">
          <!-- Column Headers -->
          <tr class="acs-data-grid__header-row">
            <!-- Auxiliary Column (checkbox for multi-select, empty otherwise) -->
            <th
              class="acs-data-grid__header-cell acs-data-grid__header-cell--auxiliary"
              [class.acs-data-grid__header-cell--selection]="selectable === 'multiple'"
              scope="col">
              <input
                *ngIf="selectable === 'multiple'"
                type="checkbox"
                [checked]="allSelected"
                [indeterminate]="someSelected"
                (change)="allSelected ? clearSelection() : selectAll()"
                aria-label="Select all rows">
            </th>

            <!-- Data Columns -->
            <th
              *ngFor="let column of columns"
              class="acs-data-grid__header-cell"
              [class.acs-data-grid__header-cell--sortable]="column.sortable && sortable"
              [class.acs-data-grid__header-cell--sorted]="currentSort?.column === column.key"
              [style.width]="column.width"
              [style.max-width]="column.maxWidth || column.width"
              [style.min-width]="column.width"
              [style.text-align]="column.align || 'left'"
              scope="col">
              
              <div class="acs-data-grid__header-content">
                <button
                  *ngIf="column.sortable && sortable; else headerLabel"
                  class="acs-data-grid__sort-button"
                  (click)="onSort(column)"
                  [attr.aria-label]="'Sort by ' + column.label"
                  [attr.aria-sort]="currentSort?.column === column.key ? (currentSort?.direction === 'asc' ? 'ascending' : 'descending') : 'none'">
                  <span class="acs-data-grid__header-label">{{ column.label }}</span>
                  <span class="acs-data-grid__sort-icon">{{ getSortIcon(column) }}</span>
                </button>

                <ng-template #headerLabel>
                  <span class="acs-data-grid__header-label">{{ column.label }}</span>
                </ng-template>
              </div>

              <!-- Column Resize Handle (moved outside header-content for full height) -->
              <div
                *ngIf="resizableColumns"
                class="acs-data-grid__resize-handle"
                (mousedown)="startColumnResize(column, $event)"
                [attr.aria-label]="'Resize column ' + column.label"
                role="separator">
                <div class="acs-data-grid__resize-handle-line"></div>
              </div>
            </th>
          </tr>

          <!-- Filter Row -->
          <tr *ngIf="showFilterRow && filterable" class="acs-data-grid__filter-row">
            <!-- Auxiliary Column (clear filters button always available) -->
            <th class="acs-data-grid__filter-cell acs-data-grid__filter-cell--auxiliary">
              <button
                *ngIf="hasActiveFilters()"
                class="acs-data-grid__clear-filters"
                (click)="clearFilters()"
                aria-label="Clear all filters"
                title="Clear all filters">
                ✕
              </button>
            </th>

            <!-- Data Column Filters -->
            <th *ngFor="let column of columns" class="acs-data-grid__filter-cell">
              <!-- Column Filter Input (skip for actions columns) -->
              <ng-container *ngIf="column.filterable !== false && column.type !== 'actions'">
                <!-- Dropdown Filter -->
                <ng-container *ngIf="shouldShowDropdownFilter(column); else textFilter">
                  <select
                    class="acs-data-grid__filter-dropdown"
                    [class.acs-data-grid__filter-dropdown--loading]="isDropdownLoading(column)"
                    [(ngModel)]="filterValues[column.key]"
                    (focus)="onDropdownFocus(column)"
                    (change)="onDropdownFilter(column, $event)"
                    [attr.aria-label]="'Filter by ' + column.label"
                    [attr.aria-describedby]="'dropdown-help-' + column.key"
                    [attr.aria-busy]="isDropdownLoading(column)"
                    [disabled]="isDropdownLoading(column)"
                    role="combobox"
                    aria-autocomplete="none"
                    [attr.aria-expanded]="true">

                    <!-- Loading option -->
                    <option *ngIf="isDropdownLoading(column)" value="" disabled>Loading...</option>

                    <!-- Error option -->
                    <option *ngIf="getDropdownError(column)" value="" disabled>{{ getDropdownError(column) }}</option>

                    <!-- Normal options -->
                    <ng-container *ngIf="!isDropdownLoading(column) && !getDropdownError(column)">
                      <option value="" disabled selected hidden>Filter {{ column.label }}...</option>
                      <option value="" aria-label="Show all items">All</option>
                      <option
                        *ngFor="let option of getDropdownOptions(column)"
                        [value]="option.raw"
                        [attr.aria-label]="'Filter by ' + option.formatted">
                        {{ option.formatted }}
                      </option>
                    </ng-container>
                  </select>

                  <!-- Screen reader help text -->
                  <span [id]="'dropdown-help-' + column.key" class="sr-only">
                    Use arrow keys to navigate options. Press Enter to apply filter.
                    <span *ngIf="isDropdownLoading(column)">Loading filter options...</span>
                    <span *ngIf="getDropdownError(column)">Error loading options: {{ getDropdownError(column) }}</span>
                  </span>

                  <!-- Loading indicator -->
                  <div *ngIf="isDropdownLoading(column)" class="acs-data-grid__filter-loading">
                    <div class="acs-data-grid__filter-spinner"></div>
                  </div>
                </ng-container>

                <!-- Text Filter (fallback) -->
                <ng-template #textFilter>
                  <input
                    type="text"
                    class="acs-data-grid__filter-input"
                    [placeholder]="'Filter ' + column.label"
                    [(ngModel)]="filterValues[column.key]"
                    (input)="onFilter(column.key, filterValues[column.key])"
                    [attr.aria-label]="'Filter by ' + column.label">
                </ng-template>
              </ng-container>
            </th>
          </tr>
        </thead>

        <!-- Body -->
        <tbody class="acs-data-grid__body" [style.transform]="'translateY(' + offsetY + 'px)'">
          <!-- Data Rows -->
          <tr 
            *ngFor="let item of displayedData; trackBy: trackByFn; let i = index"
            class="acs-data-grid__row"
            [class.acs-data-grid__row--selected]="isSelected(item)"
            [class.acs-data-grid__row--selectable]="selectable !== 'none'"
            (click)="onRowClick(item, $event)"
            (dblclick)="onRowDoubleClick(item)"
            [attr.aria-selected]="selectable !== 'none' ? isSelected(item) : null"
            role="row">

            <!-- Auxiliary Cell (checkbox for multi-select, empty otherwise) -->
            <td
              class="acs-data-grid__cell acs-data-grid__cell--auxiliary"
              [class.acs-data-grid__cell--selection]="selectable === 'multiple'">
              <input
                *ngIf="selectable === 'multiple'"
                type="checkbox"
                [checked]="isSelected(item)"
                (click)="$event.stopPropagation()"
                (change)="toggleSelection(item, $event)"
                [attr.aria-label]="'Select row ' + (i + 1)">
            </td>

            <!-- Data Cells -->
            <td
              *ngFor="let column of columns"
              class="acs-data-grid__cell"
              [class.status-cell]="isStatusColumn(column)"
              [ngClass]="getCellClass(column, item)"
              [style.width]="column.width"
              [style.max-width]="column.maxWidth || column.width"
              [style.min-width]="column.width"
              [style.text-align]="column.align || 'left'"
              [attr.data-label]="column.label">
              
              <div class="acs-data-grid__cell-content"
                   [class.acs-data-grid__cell-content--wrap]="shouldWrapColumn(column)"
                   [class.acs-data-grid__cell-content--truncate]="!shouldWrapColumn(column)"
                   [class.acs-data-grid__cell-content--break-all]="column.breakLongWords"
                   [class.acs-data-grid__cell-content--clamp]="shouldClampColumn(column)"
                   [class.acs-data-grid__cell-content--align-center]="column.align === 'center'"
                   [class.acs-data-grid__cell-content--align-right]="column.align === 'right'"
                   [class.status-badge]="isStatusColumn(column)"
                   [class.status-active]="isStatusColumn(column) && getCellValue(item, column) === 'Active'"
                   [class.status-inactive]="isStatusColumn(column) && getCellValue(item, column) === 'Inactive'"
                   [class.status-pending]="isStatusColumn(column) && getCellValue(item, column) === 'Pending'"
                   [class.status-suspended]="isStatusColumn(column) && getCellValue(item, column) === 'Suspended'"
                   [class.status-paid]="isStatusColumn(column) && getCellValue(item, column) === 'Paid'"
                   [class.status-unpaid]="isStatusColumn(column) && getCellValue(item, column) === 'Unpaid'"
                   [class.status-partial]="isStatusColumn(column) && getCellValue(item, column) === 'Partial'"
                   [class.status-cancelled]="isStatusColumn(column) && (getCellValue(item, column) === 'Cancelled' || getCellValue(item, column) === 'Charter Revoked')"
                   [class.status-refunded]="isStatusColumn(column) && getCellValue(item, column) === 'Refunded'"
                   [class.status-completed]="isStatusColumn(column) && getCellValue(item, column) === 'Completed'"
                   [class.status-failed]="isStatusColumn(column) && getCellValue(item, column) === 'Failed'"
                   [class.status-processing]="isStatusColumn(column) && getCellValue(item, column) === 'Processing'"
                   [style.max-width]="column.maxWidth || null"
                   [style.-webkit-line-clamp]="getMaxLines(column)"
                   [title]="shouldShowTooltip(column) ? getCellValue(item, column) : ''"
                   (mouseenter)="checkTextOverflow($event, item, column)"
                   #cellContent>
                <ng-container *ngIf="column.type === 'actions'; else dataCell">
                  <div class="acs-data-grid__actions">
                    <button
                      *ngFor="let action of column.actions"
                      class="acs-data-grid__action-btn"
                      [title]="action.label"
                      [disabled]="action.disabled ? action.disabled(item) : false"
                      (click)="$event.stopPropagation(); action.handler(item)">
                      {{ action.icon }}
                    </button>
                  </div>
                </ng-container>

                <ng-template #dataCell>
                  <ng-container *ngIf="column.type !== 'custom'; else customCell">
                    {{ getCellValue(item, column) }}
                  </ng-container>

                  <ng-template #customCell>
                    <ng-content select="[slot=cell-{{ column.key }}]"></ng-content>
                  </ng-template>
                </ng-template>
              </div>
            </td>
          </tr>

          <!-- Empty State - removed from table, will be overlay -->
        </tbody>
      </table>

      <!-- Empty State Overlay for Grid -->
      <div *ngIf="!effectiveLoading && displayedData.length === 0" class="acs-data-grid__empty-overlay">
        <div class="acs-data-grid__empty-content">
          <div class="acs-data-grid__empty-icon">📄</div>
          <div class="acs-data-grid__empty-text">{{ emptyText }}</div>
        </div>
      </div>

      <!-- Infinite Scroll Loading Indicator -->
      <div *ngIf="serverSide && serverLoading && displayedData.length > 0" class="acs-data-grid__infinite-loader">
        <div class="acs-data-grid__infinite-loader-content">
          <acs-loading-spinner size="small" message="Loading more data..." />
        </div>
      </div>
    </div>
  </div>

  <!-- Card View -->
  <div class="acs-data-grid__cards" *ngIf="viewMode === 'card'">
    <div 
      *ngFor="let item of displayedData; trackBy: trackByFn"
      class="acs-data-grid__card"
      [class.acs-data-grid__card--selected]="isSelected(item)"
      (click)="onRowClick(item, $event)"
      (dblclick)="onRowDoubleClick(item)">
      
      <!-- Selection Checkbox -->
      <div *ngIf="selectable === 'multiple'" class="acs-data-grid__card-selection">
        <input 
          type="checkbox"
          [checked]="isSelected(item)"
          (click)="$event.stopPropagation()"
          (change)="toggleSelection(item, $event)">
      </div>

      <!-- Card Content -->
      <div class="acs-data-grid__card-content">
        <ng-container *ngIf="!cardTemplate; else customCard">
          <!-- Enhanced Card Layout for Member/Officer Data -->
          <ng-container *ngIf="isPersonCard(item); else enhancedCard">
            <!-- Person Name (First + Last combined) -->
            <div class="info-card__name">
              <span class="info-card__icon info-card__icon--membership">👤</span>
              {{ getPersonName(item) }}
            </div>
            <!-- Person Details -->
            <div *ngFor="let column of getPersonCardColumns()" 
                 class="info-card__detail"
                 [ngClass]="getCardDetailClass(column)">
              <span class="info-card__icon" [ngClass]="getCardIconClass(column)">{{ getCardIcon(column) }}</span>
              <ng-container *ngIf="column.key === 'email'; else regularDetail">
                <a [href]="'mailto:' + getCellValue(item, column)" class="info-card__email">
                  {{ getCellValue(item, column) }}
                </a>
              </ng-container>
              <ng-template #regularDetail>
                {{ getCellValue(item, column) }}
              </ng-template>
            </div>
          </ng-container>
          
          <ng-template #enhancedCard>
            <!-- Enhanced Card Layout for All Other Data -->
            <div *ngFor="let column of getDisplayColumns(); let first = first" 
                 class="info-card__detail"
                 [ngClass]="{'info-card__name': first, 'info-card__detail': !first}">
              <span class="info-card__icon" [ngClass]="getCardIconClass(column)">{{ getCardIcon(column) }}</span>
              <ng-container *ngIf="column.key === 'email'; else regularDetail">
                <a [href]="'mailto:' + getCellValue(item, column)" class="info-card__email">
                  {{ getCellValue(item, column) }}
                </a>
              </ng-container>
              <ng-template #regularDetail>
                <span [ngClass]="getCardDetailClass(column)">
                  {{ getCellValue(item, column) }}
                </span>
              </ng-template>
            </div>
          </ng-template>
        </ng-container>
        
        <ng-template #customCard>
          <ng-container *ngTemplateOutlet="cardTemplate; context: { $implicit: item }"></ng-container>
        </ng-template>
      </div>
    </div>

    <!-- Empty State for Cards -->
    <div *ngIf="!effectiveLoading && displayedData.length === 0" class="acs-data-grid__cards-empty">
      <div class="acs-data-grid__empty-content">
        <div class="acs-data-grid__empty-icon">📄</div>
        <div class="acs-data-grid__empty-text">{{ emptyText }}</div>
      </div>
    </div>

    <!-- Infinite Scroll Loading Indicator for Cards -->
    <div *ngIf="serverSide && serverLoading && displayedData.length > 0" class="acs-data-grid__infinite-loader">
      <div class="acs-data-grid__infinite-loader-content">
        <acs-loading-spinner size="small" message="Loading more data..." />
      </div>
    </div>
  </div>

  <!-- Footer -->
  <div class="acs-data-grid__footer" *ngIf="effectiveTotalCount > 0">
    <div class="acs-data-grid__info">
      <span *ngIf="serverSide">Showing {{ displayedData.length }} of {{ effectiveTotalCount }} items</span>
      <span *ngIf="!serverSide">Showing {{ displayedData.length }} of {{ filteredData.length }} items
        <span *ngIf="filteredData.length !== data.length">(filtered from {{ data.length }} total)</span>
      </span>
    </div>
  </div>
</div>

<!-- Simple Export Progress Indicator -->
<div class="acs-data-grid__export-indicator"
     *ngIf="isExporting">
  <div class="acs-data-grid__export-indicator-content">
    <span class="acs-data-grid__export-indicator-icon">📥</span>
    <span class="acs-data-grid__export-indicator-text">
      {{ exportProgressMessage }}
    </span>
    <div class="acs-data-grid__export-indicator-spinner">
      <acs-loading-spinner size="small" />
    </div>
  </div>
</div>

